import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Header from "@user/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MessageSquare, PhoneCall, Video, ShieldCheck, Lock, Calendar, Clock } from "lucide-react";

const TYPES = [
  { key: "chat", label: "Chat", Icon: MessageSquare, desc: "Text messages with lawyer", mult: 1.0 },
  { key: "audio", label: "Audio Call", Icon: PhoneCall, desc: "Voice call (20 min)", mult: 1.2 },
  { key: "video", label: "Video Call", Icon: Video, desc: "Face-to-face (30 min)", mult: 1.5 },
];

const SLOTS = ["10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const loadRzp = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Booking() {
  const { lawyerId } = useParams();
  const [sp] = useSearchParams();
  const initialType = sp.get("type") || "chat";
  const isInstant = sp.get("mode") === "instant";
  const { user, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [lawyer, setLawyer] = useState(null);
  const [type, setType] = useState(initialType);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) login();
  }, [authLoading, user, login]);

  useEffect(() => {
    api.get(`/lawyers/${lawyerId}`).then((r) => setLawyer(r.data)).catch(() => setLawyer(null));
  }, [lawyerId]);

  // default date = today
  useEffect(() => {
    const d = new Date();
    setDate(d.toISOString().slice(0, 10));
  }, []);

  if (!lawyer || !user) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-2xl mx-auto p-8 text-center text-slate-500">Loading…</div>
      </div>
    );
  }

  const selectedType = TYPES.find((t) => t.key === type);
  // Pricing comes pre-computed from backend. Falls back to legacy fee math
  // if the lawyer doc somehow lacks `pricing` (shouldn't happen post-migration).
  const typePricing =
    lawyer.pricing?.[type] ??
    (() => {
      const original = Math.round(lawyer.fee * selectedType.mult);
      return { original, offer: original, save_amount: 0, save_pct: 0, applied: "none" };
    })();
  const amount = typePricing.offer;
  const originalAmount = typePricing.original;
  const youSave = typePricing.save_amount;
  const isFirstOffer = typePricing.applied === "first" || !!lawyer.is_first_consultation;
  const platformFee = Math.round(amount * 0.05);
  const total = amount + platformFee;

  const book = async () => {
    if (!query.trim()) return toast.error("Please describe your legal issue");
    if (!isInstant && !slot) return toast.error("Please select a time slot");
    if (isInstant && !lawyer.available) {
      toast.error("This lawyer just went offline. Please book a slot instead.");
      navigate(`/book/${lawyer.id}`);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        lawyer_id: lawyer.id,
        query,
        consultation_type: type,
        booking_type: isInstant ? "instant" : "scheduled",
      };
      if (!isInstant) {
        payload.slot_date = date;
        payload.slot_time = slot;
      }
      const createRes = await api.post("/consultations", payload);
      const cid = createRes.data.id;

      const ok = await loadRzp();
      if (!ok) { toast.error("Could not load payment gateway"); setBusy(false); return; }

      const orderRes = await api.post(`/consultations/${cid}/order`);
      const { order_id, amount: amountPaise, currency, key_id } = orderRes.data;

      const options = {
        key: key_id,
        amount: amountPaise,
        currency,
        order_id,
        name: "Advoset",
        description: `${selectedType.label} with ${lawyer.name}`,
        theme: { color: "#0A2540" },
        prefill: { name: user.name, email: user.email },
        handler: async (resp) => {
          try {
            const verifyRes = await api.post(`/consultations/${cid}/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            // A/B conversion tracking
            const session = localStorage.getItem("adv_exp_session") || "";
            const priceVariant = localStorage.getItem("adv_exp_chat_entry_price");
            if (priceVariant) {
              api.post("/experiments/event", { key: "chat_entry_price", variant: priceVariant, event: "conversion", session }).catch(() => {});
            }
            const ctaVariant = localStorage.getItem("adv_exp_cta_copy");
            if (ctaVariant) {
              api.post("/experiments/event", { key: "cta_copy", variant: ctaVariant, event: "conversion", session }).catch(() => {});
            }
            const headlineVariant = localStorage.getItem("adv_exp_hero_headline");
            if (headlineVariant) {
              api.post("/experiments/event", { key: "hero_headline", variant: headlineVariant, event: "conversion", session }).catch(() => {});
            }
            toast.success("Payment successful!");
            navigate(`/consultations/${cid}?invoice=${verifyRes.data.invoice_no}`);
          } catch (e) {
            toast.error("Payment verification failed");
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10 grid lg:grid-cols-5 gap-6">
        {/* LEFT: form */}
        <div className="lg:col-span-3 space-y-4">
          <div>
            <span className="adv-chip">{isInstant ? "Instant Consultation" : "Book a Consultation"}</span>
            <h1 className="mt-3 text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
              Talk to {lawyer.name.split(" ").slice(1).join(" ")}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{lawyer.specialization} · {lawyer.city}</p>
          </div>

          {isInstant && (
            <div data-testid="instant-banner" className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <span className="h-9 w-9 rounded-xl bg-emerald-600 text-white grid place-items-center shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"/></svg>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-emerald-900">Available now · you'll start the call right after payment</p>
                <p className="text-xs text-emerald-800/80">Skip slot selection — connect immediately with {lawyer.name.split(" ").slice(0,2).join(" ")}.</p>
              </div>
            </div>
          )}

          {/* Type */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">1 · Select consultation type</p>
            <div className="grid grid-cols-3 gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setType(t.key)}
                  data-testid={`type-${t.key}`}
                  className={`rounded-xl border p-4 text-left transition-all ${type === t.key ? "border-[#0A2540] bg-[#0A2540] text-white shadow-lg" : "border-slate-200 bg-white hover:border-[#0A2540]"}`}
                >
                  <t.Icon size={20} className={type === t.key ? "text-[#D4AF37]" : "text-[#0A2540]"} />
                  <p className="mt-2 font-bold text-sm">{t.label}</p>
                  <p className={`text-xs ${type === t.key ? "text-white/70" : "text-slate-500"}`}>{t.desc}</p>
                  <p className={`mt-1 text-xs font-bold ${type === t.key ? "text-[#D4AF37]" : "text-[#0A2540]"}`}>₹{lawyer.pricing?.[t.key]?.offer ?? Math.round((lawyer.fee || 500) * t.mult)}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Date + Slot — skipped for instant consultations */}
          {!isInstant && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">2 · Pick a time slot</p>
            <div className="flex gap-3 mb-3">
              <div className="flex-1">
                <Label className="text-xs flex items-center gap-1"><Calendar size={12} /> Date</Label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  data-testid="booking-date"
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                />
              </div>
            </div>
            <Label className="text-xs flex items-center gap-1"><Clock size={12} /> Available slots</Label>
            <div className="mt-2 grid grid-cols-4 sm:grid-cols-8 gap-2">
              {SLOTS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSlot(s)}
                  data-testid={`slot-${s}`}
                  className={`px-2 py-2 rounded-lg text-xs font-bold border transition-all ${slot === s ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white border-slate-200 text-slate-700 hover:border-[#0A2540]"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Query */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">{isInstant ? "2" : "3"} · Describe your issue</p>
            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Briefly describe your legal matter…"
              data-testid="booking-query"
              className="min-h-[100px]"
            />
            <p className="mt-2 text-[11px] text-slate-400 flex items-center gap-1"><Lock size={12} /> Only the lawyer will see this.</p>
          </div>
        </div>

        {/* RIGHT: summary */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-20 bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
            <h3 className="font-bold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Order summary</h3>
            <div className="flex justify-between text-sm items-baseline">
              <span className="text-slate-500">{selectedType.label} · {lawyer.name}</span>
              <span className="inline-flex items-baseline gap-2">
                {youSave > 0 && (
                  <span className="text-xs text-slate-400 line-through" data-testid="summary-original">₹{originalAmount}</span>
                )}
                <span className="font-bold text-[#0A2540]" data-testid="summary-amount">₹{amount}</span>
              </span>
            </div>
            {youSave > 0 && (
              <div className="flex items-center gap-2 -mt-2 -mb-1" data-testid="summary-save">
                {isFirstOffer ? (
                  <span data-testid="summary-first-offer-badge" className="text-[10px] font-bold uppercase tracking-wider bg-[#FBF4DF] text-[#0A2540] border border-[#F1E3B5] rounded-full px-2 py-0.5">
                    🎉 First Consultation Offer · You save ₹{youSave}
                  </span>
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">
                    You save ₹{youSave} · {typePricing.save_pct}% OFF
                  </span>
                )}
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Platform fee (5%)</span>
              <span className="font-bold text-[#0A2540]">₹{platformFee}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">GST</span>
              <span className="font-bold text-[#0A2540]">Included</span>
            </div>
            <div className="h-px bg-slate-100" />
            <div className="flex justify-between items-end">
              <span className="text-xs uppercase tracking-wider font-bold text-slate-400">Total</span>
              <span className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>₹{total}</span>
            </div>
            <Button
              onClick={book}
              disabled={busy}
              data-testid="confirm-booking-btn"
              className="w-full rounded-xl adv-btn-gold font-bold h-11"
            >
              {busy ? "Processing…" : isInstant ? `Pay ₹${total} & Start Now` : `Confirm Booking · ₹${total}`}
            </Button>
            <div className="flex items-center justify-center gap-3 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1"><ShieldCheck size={12} className="text-emerald-600" /> Secure Razorpay</span>
              <span className="inline-flex items-center gap-1"><Lock size={12} className="text-[#D4AF37]" /> 100% private</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
